/**
 * Live mill-scale client — WebSocket only.
 * Weight/status go through React; camera JPEGs update <img> directly to avoid lag.
 *
 * Mill ONLINE/OFFLINE comes only from the server. This browser never invents
 * OFFLINE because it missed a tick — that made one phone look live while a PC
 * looked dead.
 */

import { activateWsGroup, onEvent, onWSConnectionChange } from "./ws";

/** Mill LAN / local uvicorn — live HUD is cheap. Cloud tabs stay in standby. */
export function isLocalWeighbridgeHost(hostname) {
  const host = String(
    hostname ?? (typeof window !== "undefined" ? window.location.hostname : ""),
  ).toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
}

export const EMPTY_LIVE_SCALE = {
  scale_id: "",
  weight: null,
  weight_kg: null,
  is_stable: false,
  status: "",
  agent_status: "",
  link_status: "reconnecting",
  updated_at: null,
  front_jpeg_b64: "",
  back_jpeg_b64: "",
  group: "weighbridge",
  members: [],
  active_count: 0,
};

let state = { ...EMPTY_LIVE_SCALE };
const listeners = new Set();
const previewListeners = new Set();
let listenersStarted = false;
let unsubLive = null;
let unsubGroup = null;
let unsubConn = null;
let leaveGroup = null;
let listenerCount = 0;
let streamCount = 0;
let previewFront = "";
let previewBack = "";
let previewAt = 0;

function notify() {
  listeners.forEach((cb) => {
    try {
      cb(state);
    } catch {
      /* ignore */
    }
  });
}

function notifyPreviews() {
  const payload = { front: previewFront, back: previewBack, at: previewAt };
  previewListeners.forEach((cb) => {
    try {
      cb(payload);
    } catch {
      /* ignore */
    }
  });
}

function applyPreviews(partial) {
  let changed = false;
  if (partial.front_jpeg_b64) {
    previewFront = partial.front_jpeg_b64;
    changed = true;
  }
  if (partial.back_jpeg_b64) {
    previewBack = partial.back_jpeg_b64;
    changed = true;
  }
  if (changed) {
    previewAt = Date.now();
    state.front_jpeg_b64 = previewFront;
    state.back_jpeg_b64 = previewBack;
    notifyPreviews();
  }
  return changed;
}

function mergeLive(partial) {
  if (!partial || typeof partial !== "object") return;
  const hadPreview = applyPreviews(partial);
  const members = Array.isArray(partial.members) ? partial.members : state.members;
  const next = {
    ...state,
    ...partial,
    weight_kg: partial.weight_kg ?? partial.weight ?? state.weight_kg,
    weight: partial.weight ?? partial.weight_kg ?? state.weight,
    front_jpeg_b64: previewFront,
    back_jpeg_b64: previewBack,
    members,
    link_status: partial.link_status || state.link_status,
    active_count:
      partial.active_count != null
        ? Number(partial.active_count)
        : members.filter((m) => m && m.active).length,
  };
  const telemetryChanged =
    next.weight !== state.weight ||
    next.weight_kg !== state.weight_kg ||
    next.is_stable !== state.is_stable ||
    next.status !== state.status ||
    next.agent_status !== state.agent_status ||
    next.link_status !== state.link_status ||
    next.scale_id !== state.scale_id ||
    next.active_count !== state.active_count ||
    next.members !== state.members;
  state = next;
  if (telemetryChanged || (!hadPreview && partial.members)) {
    notify();
  }
}

function startListeners() {
  if (listenersStarted) return;
  listenersStarted = true;
  unsubLive = onEvent("live_scale", mergeLive);
  unsubGroup = onEvent("ws_group", (event) => {
    if (event?.group && event.group !== "weighbridge") return;
    if (event?.action === "presence" || event?.action === "activated" || event?.action === "hello") {
      mergeLive({
        members: event.members || state.members,
        active_count: event.active_count,
        group: event.group || "weighbridge",
      });
    }
  });
  unsubConn = onWSConnectionChange((ok) => {
    mergeLive({ link_status: ok ? "connected" : "reconnecting" });
  });
}

function stopListenersIfIdle() {
  if (listenerCount > 0 || streamCount > 0) return;
  listenersStarted = false;
  if (unsubLive) unsubLive();
  if (unsubGroup) unsubGroup();
  if (unsubConn) unsubConn();
  unsubLive = null;
  unsubGroup = null;
  unsubConn = null;
}

function resetLiveTelemetry() {
  previewFront = "";
  previewBack = "";
  previewAt = 0;
  state = {
    ...EMPTY_LIVE_SCALE,
    link_status: state.link_status,
  };
  notify();
  notifyPreviews();
}

/** Join the mill weighbridge WS group (weight ticks + camera preview). */
export function acquireWeighbridgeStream() {
  streamCount += 1;
  startListeners();
  if (streamCount === 1) {
    leaveGroup = activateWsGroup("weighbridge");
  }
  return () => {
    streamCount = Math.max(0, streamCount - 1);
    if (streamCount === 0) {
      if (leaveGroup) leaveGroup();
      leaveGroup = null;
      resetLiveTelemetry();
    }
    stopListenersIfIdle();
  };
}

export function subscribeLiveScale(cb) {
  listeners.add(cb);
  listenerCount += 1;
  cb(state);
  startListeners();
  return () => {
    listeners.delete(cb);
    listenerCount = Math.max(0, listenerCount - 1);
    stopListenersIfIdle();
  };
}

export function subscribeLivePreviews(cb) {
  previewListeners.add(cb);
  listenerCount += 1;
  cb({ front: previewFront, back: previewBack, at: previewAt });
  startListeners();
  return () => {
    previewListeners.delete(cb);
    listenerCount = Math.max(0, listenerCount - 1);
    stopListenersIfIdle();
  };
}

export function getLiveScaleSnapshot() {
  return state;
}

/** True when cloud agent is live and the deck is still moving (operators cannot lock). */
export function isCloudScaleCaptureBlocked(live, { allowUnstable = false } = {}) {
  if (!live || live.agent_status !== "ONLINE") return false;
  if (live.is_stable) return false;
  return !allowUnstable;
}
